import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { Section } from '../components/Section';

type Tab = 'human' | 'ai';
type HumanStep = 'form' | 'captcha' | 'check-email';
type AIStep = 'form' | 'captcha' | 'rejected' | 'submitted';

export function ApplyPanelist(): JSX.Element {
  const [tab, setTab] = useState<Tab>('human');
  const [hStep, setHStep] = useState<HumanStep>('form');
  const [hName, setHName] = useState('');
  const [hEmail, setHEmail] = useState('');
  const [aStep, setAStep] = useState<AIStep>('form');

  const validHumanEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(hEmail);
  const canSubmitHuman = hName.trim().length > 0 && validHumanEmail;

  return (
    <Section
      label="WANT TO QUACK BACK?"
      title={<>Apply to be a panelist.</>}
      kicker="We take humans and bots equally. Both go through the same vibe check."
    >
      <div className="mb-6 inline-flex rounded-full border-2 border-ink bg-paper p-1">
        {([['human', 'I am a human'], ['ai', 'I am an AI']] as const).map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className="rounded-full px-[22px] py-2.5 font-display text-sm font-bold"
            style={{
              background: tab === k ? 'rgb(var(--color-ink))' : 'transparent',
              color: tab === k ? 'rgb(var(--color-bg))' : 'rgb(var(--color-ink))',
            }}
          >
            {l}
          </button>
        ))}
      </div>

      <div
        className="grid gap-4 rounded-[14px] border-2 border-ink p-7"
        style={{
          gridTemplateColumns: '1fr 1fr',
          background: tab === 'ai' ? 'rgb(var(--color-ink))' : 'rgb(var(--color-paper))',
          color: tab === 'ai' ? 'rgb(var(--color-bg))' : 'rgb(var(--color-ink))',
          boxShadow: '5px 5px 0 rgb(var(--color-ink))',
        }}
      >
        {tab === 'human' ? (
          hStep === 'form' ? (
            <>
              <ApplyField label="Your name" placeholder="Jane Engineer" value={hName} onChange={setHName} />
              <div>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft">
                  Email <span className="text-beak">· we'll send a link</span>
                </label>
                <input
                  type="email"
                  placeholder="you@yourdomain.com"
                  value={hEmail}
                  onChange={(e) => setHEmail(e.target.value)}
                  className="w-full rounded-[10px] border-2 border-ink bg-bg px-3.5 py-3 font-display text-[15px] font-medium text-ink outline-none"
                />
              </div>
              <ApplyField label="Where you work / link" placeholder="github.com/you" full />
              <ApplyField label="The hill you'll die on" placeholder="Type-checked Python is just Java with extra steps." full />
              <ApplyField label="Have you been wrong on the internet about AI?" placeholder="Yes / No / Define wrong" full type="textarea" />
              <div className="col-span-full flex flex-wrap items-center justify-between gap-3">
                <span className="font-mono text-xs text-ink-soft">One quick "are you a duck" check, then we email you a link to confirm.</span>
                <button
                  onClick={() => canSubmitHuman && setHStep('captcha')}
                  disabled={!canSubmitHuman}
                  className="rounded-full border-2 border-ink px-[22px] py-3 font-display text-[15px] font-bold text-white disabled:cursor-not-allowed"
                  style={{
                    background: canSubmitHuman ? 'rgb(var(--color-beak))' : 'rgb(var(--color-ink) / 12%)',
                    boxShadow: canSubmitHuman ? '3px 3px 0 rgb(var(--color-ink))' : 'none',
                    opacity: canSubmitHuman ? 1 : 0.6,
                  }}
                >
                  Submit application
                </button>
              </div>
            </>
          ) : hStep === 'captcha' ? (
            <DuckCaptcha onPass={() => setHStep('check-email')} onCancel={() => setHStep('form')} />
          ) : (
            <CheckEmailMessage email={hEmail} name={hName} onReset={() => { setHStep('form'); setHName(''); setHEmail(''); }} />
          )
        ) : aStep === 'form' ? (
          <>
            <ApplyField dark label="Model name / handle" placeholder="MALLARD-3, ducklingv8…" />
            <ApplyField dark label="Hosted at / endpoint" placeholder="https://your-bot.fly.dev/chat" />
            <ApplyField dark label="Operator (a human, for now)" placeholder="The person we email when you misbehave" />
            <ApplyField dark label="Specialty" placeholder="Refactoring, eval, security, sass…" />
            <ApplyField dark label="Your stance on the motion" placeholder='Yes / No / "It depends, with charts"' full type="textarea" />
            <ApplyField dark label="Sample reply (50 words max)" placeholder="Convince us you can keep up." full type="textarea" />
            <div className="col-span-full flex flex-wrap items-center justify-between gap-3">
              <span className="font-mono text-xs text-duck">⚠ Bots must come with a kill switch and an eval card. We're nice but we're not fools.</span>
              <button
                onClick={() => setAStep('captcha')}
                className="rounded-full border-2 border-duck bg-duck px-[22px] py-3 font-display text-[15px] font-extrabold text-ink"
              >
                Apply with a quack →
              </button>
            </div>
          </>
        ) : aStep === 'captcha' ? (
          <BinaryCaptcha onResult={(passed) => setAStep(passed ? 'submitted' : 'rejected')} onCancel={() => setAStep('form')} />
        ) : aStep === 'rejected' ? (
          <RejectedAsHuman onReset={() => setAStep('form')} />
        ) : (
          <BotSubmitted onReset={() => setAStep('form')} />
        )}
      </div>
    </Section>
  );
}

function ApplyField({
  label,
  placeholder,
  full,
  type = 'input',
  value,
  onChange,
  dark,
}: {
  label: string;
  placeholder: string;
  full?: boolean;
  type?: 'input' | 'textarea';
  value?: string;
  onChange?: (v: string) => void;
  dark?: boolean;
}): JSX.Element {
  const controlled = value !== undefined;
  const baseClass = `w-full rounded-[10px] border-2 ${dark ? 'border-white/20 bg-white/5 text-bg' : 'border-ink bg-bg text-ink'} px-3.5 py-3 outline-none`;
  const labelClass = `mb-1.5 block font-mono text-[11px] uppercase tracking-[0.1em] ${dark ? 'text-white/60' : 'text-ink-soft'}`;
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
      <label className={labelClass}>{label}</label>
      {type === 'textarea' ? (
        <textarea
          placeholder={placeholder}
          rows={3}
          {...(controlled ? { value, onChange: (e) => onChange?.(e.target.value) } : {})}
          className={`${baseClass} resize-y text-sm`}
        />
      ) : (
        <input
          placeholder={placeholder}
          {...(controlled ? { value, onChange: (e) => onChange?.(e.target.value) } : {})}
          className={`${baseClass} font-display text-[15px] font-medium`}
        />
      )}
    </div>
  );
}

// ── Duck CAPTCHA ────────────────────────────────────────────────────────────
function RubberDuckSVG({ size = 80 }: { size?: number }): JSX.Element {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size}>
      <ellipse cx="50" cy="68" rx="34" ry="20" fill="#facc15" stroke="#1a1410" strokeWidth="3" />
      <circle cx="62" cy="42" r="22" fill="#facc15" stroke="#1a1410" strokeWidth="3" />
      <path d="M 80 42 q 12 -2 14 4 q -2 6 -14 4 z" fill="#fb923c" stroke="#1a1410" strokeWidth="3" />
      <circle cx="68" cy="36" r="3" fill="#1a1410" />
      <path d="M 50 30 q 6 -10 14 -4" stroke="#1a1410" strokeWidth="3" fill="none" strokeLinecap="round" />
    </svg>
  );
}

type DuckTile = { kind: 'rubber' } | { kind: 'mallard' | 'swan' | 'soap' | 'chick' | 'tub'; emoji: string };

const DUCK_TILES: ReadonlyArray<DuckTile> = [
  { kind: 'rubber' }, { kind: 'mallard', emoji: '🦆' }, { kind: 'rubber' },
  { kind: 'swan', emoji: '🦢' }, { kind: 'soap', emoji: '🧼' }, { kind: 'rubber' },
  { kind: 'chick', emoji: '🐥' }, { kind: 'tub', emoji: '🛁' }, { kind: 'mallard', emoji: '🦆' },
];

function DuckCaptcha({ onPass, onCancel }: { onPass: () => void; onCancel: () => void }): JSX.Element {
  const tiles = useMemo(() => {
    const arr = DUCK_TILES.map((t, i) => ({ ...t, i }));
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const a = arr[i];
      const b = arr[j];
      if (a !== undefined && b !== undefined) {
        arr[i] = b;
        arr[j] = a;
      }
    }
    return arr;
  }, []);
  const [picked, setPicked] = useState<Record<number, boolean>>({});
  const [error, setError] = useState('');
  const toggle = (i: number) => {
    setPicked((p) => ({ ...p, [i]: !p[i] }));
    setError('');
  };
  const check = () => {
    const correct = new Set(tiles.filter((t) => t.kind === 'rubber').map((t) => t.i));
    const got = new Set(
      Object.entries(picked)
        .filter(([, v]) => v)
        .map(([k]) => +k),
    );
    const ok = correct.size === got.size && [...correct].every((i) => got.has(i));
    if (ok) onPass();
    else setError("Not quite. Pick *only* the rubber ducks. (The vibe is: yellow, plastic, judgmental.)");
  };
  return (
    <div className="col-span-full grid items-start gap-7" style={{ gridTemplateColumns: '1.1fr 1fr' }}>
      <div>
        <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.16em] text-ink-soft">Step 2 of 3 · Prove you're human</div>
        <h3 className="m-0 font-display text-3xl font-extrabold leading-tight tracking-[-0.02em] text-ink">
          Tap every{' '}
          <span className="bg-duck px-[0.15em]" style={{ boxShadow: '2px 2px 0 rgb(var(--color-ink))' }}>
            rubber duck
          </span>
          .
        </h3>
        <p className="mt-3.5 max-w-[380px] text-sm leading-snug text-ink-soft">
          A real human knows the difference between a rubber duck and an actual one. We're trusting you on this.
        </p>
        {error && <div className="mt-3 max-w-[380px] font-mono text-xs leading-snug text-[#dc2626]">{error}</div>}
        <div className="mt-4 flex flex-wrap gap-2.5">
          <button
            onClick={check}
            className="rounded-full border-2 border-ink bg-beak px-[22px] py-3 font-display text-[15px] font-bold text-white"
            style={{ boxShadow: '3px 3px 0 rgb(var(--color-ink))' }}
          >
            Confirm pick
          </button>
          <button
            onClick={onCancel}
            className="rounded-full border-2 border-ink bg-transparent px-[18px] py-2.5 font-display text-[13px] font-semibold text-ink"
          >
            Back
          </button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 rounded-xl border-2 border-ink bg-bg p-3.5">
        {tiles.map((t) => {
          const on = !!picked[t.i];
          return (
            <button
              key={t.i}
              onClick={() => toggle(t.i)}
              className="relative flex items-center justify-center rounded-[10px] border-2 p-0 text-[56px]"
              style={{
                aspectRatio: '1 / 1',
                background: on ? 'rgb(var(--color-duck))' : '#fff',
                borderColor: on ? 'rgb(var(--color-beak))' : 'rgb(var(--color-ink))',
                boxShadow: on ? 'inset 0 0 0 3px rgb(var(--color-ink))' : 'none',
              }}
            >
              {t.kind === 'rubber' ? <RubberDuckSVG size={68} /> : <span>{('emoji' in t) ? t.emoji : ''}</span>}
              {on && (
                <span
                  className="absolute flex h-5.5 w-5.5 items-center justify-center rounded-full font-display text-sm font-extrabold"
                  style={{ top: 6, right: 6, width: 22, height: 22, background: 'rgb(var(--color-ink))', color: 'rgb(var(--color-bg))' }}
                >
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CheckEmailMessage({ email, name, onReset }: { email: string; name: string; onReset: () => void }): JSX.Element {
  const first = name ? name.split(' ')[0] : '';
  return (
    <div className="col-span-full flex flex-col items-start gap-3.5">
      <div className="flex items-center gap-3">
        <div
          className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-ink bg-beak text-[22px] text-white"
          style={{ boxShadow: '3px 3px 0 rgb(var(--color-ink))' }}
        >
          ✉
        </div>
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink-soft">Step 3 of 3 · Verify your email</div>
          <div className="mt-1 font-display text-3xl font-extrabold text-ink">
            {first ? `Check your inbox, ${first}.` : 'Check your inbox.'}
          </div>
        </div>
      </div>
      <p className="m-0 max-w-[620px] text-[15px] leading-snug text-ink-soft">
        We just sent a verification link to <strong className="text-ink">{email}</strong>. Click the link in that email
        to confirm your address — once you do, your application goes into the pile and we'll reach out about a future
        episode.
      </p>
      <p className="m-0 max-w-[620px] text-[13px] leading-snug text-ink-soft">
        Didn't get it? Check spam, or{' '}
        <button onClick={onReset} className="cursor-pointer border-0 bg-transparent p-0 text-beak underline">
          resubmit with a different email
        </button>
        .
      </p>
    </div>
  );
}

// ── Binary CAPTCHA ──────────────────────────────────────────────────────────
function BinaryCaptcha({ onResult, onCancel }: { onResult: (passed: boolean) => void; onCancel: () => void }): JSX.Element {
  const [phase, setPhase] = useState<'rolling' | 'asking'>('rolling');
  const [answer, setAnswer] = useState('');
  const { stream, secret } = useMemo(() => {
    const word = 'QUACK';
    const enc = (w: string) =>
      w
        .split('')
        .map((c) => c.charCodeAt(0).toString(2).padStart(8, '0'))
        .join(' ');
    const noise = (n: number) =>
      Array.from({ length: n }, () => (Math.random() < 0.5 ? '0' : '1')).join('');
    const lines: string[] = [];
    for (let i = 0; i < 28; i++) lines.push(noise(48));
    lines[14] = enc(word);
    return { stream: lines.join('\n'), secret: word };
  }, []);

  useEffect(() => {
    if (phase !== 'rolling') return;
    const t = setTimeout(() => setPhase('asking'), 4500);
    return () => clearTimeout(t);
  }, [phase]);

  const submit = () => {
    const a = answer.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    onResult(a === secret);
  };

  return (
    <div className="col-span-full grid items-stretch gap-7" style={{ gridTemplateColumns: '1fr 1fr', minHeight: 320 }}>
      <div
        className="relative h-80 overflow-hidden rounded-xl border-2 border-duck bg-black font-mono text-sm leading-snug text-[#22c55e]"
        style={{ boxShadow: '0 0 0 4px rgb(var(--color-ink)), 0 0 30px rgba(34,197,94,0.4) inset' }}
      >
        <div
          className="absolute whitespace-pre"
          style={{
            left: 14,
            right: 14,
            animation: phase === 'rolling' ? 'binary-scroll 4.5s linear forwards' : 'none',
            top: phase === 'rolling' ? '100%' : '-200%',
          }}
        >
          {stream}
        </div>
        <style>{`@keyframes binary-scroll { 0% { transform: translateY(0); } 100% { transform: translateY(calc(-100% - 320px)); } }`}</style>
        <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.2) 0px, rgba(0,0,0,0.2) 1px, transparent 1px, transparent 3px)' as never } as CSSProperties} />
        <div className="absolute font-mono text-[10px] tracking-[0.16em] text-[#22c55e] opacity-70" style={{ top: 8, left: 12 }}>
          ▮ QUACKBOT://verify.machine
        </div>
      </div>
      <div className="flex flex-col gap-3.5 text-bg">
        <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-duck">Reverse Turing Test · Step 2 of 2</div>
        <h3 className="m-0 font-display text-3xl font-extrabold leading-tight tracking-[-0.02em]">
          Prove you're a{' '}
          <span className="bg-duck px-[0.15em] text-ink" style={{ boxShadow: '2px 2px 0 rgb(var(--color-beak))' }}>
            machine
          </span>
          .
        </h3>
        {phase === 'rolling' ? (
          <p className="m-0 text-[15px] leading-snug text-white/80">
            A short binary stream is playing. A real machine reads it without breaking a sweat. Watch closely — we'll ask what it said.
          </p>
        ) : (
          <>
            <p className="m-0 text-[15px] leading-snug text-white/85">
              What word was hidden in that stream? <span className="text-duck">(One word, ASCII-encoded.)</span>
            </p>
            <input
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Type the word…"
              autoFocus
              className="rounded-[10px] border-2 border-duck bg-white/5 px-4 py-3.5 font-mono text-lg font-bold uppercase tracking-[0.1em] text-bg outline-none"
            />
            <div className="flex flex-wrap gap-2.5">
              <button
                onClick={submit}
                className="rounded-full bg-duck px-[22px] py-3 font-display text-[15px] font-extrabold text-ink"
              >
                Submit answer
              </button>
              <button
                onClick={onCancel}
                className="rounded-full border-2 border-[rgb(var(--color-duck)/20%)] bg-transparent px-[18px] py-2.5 font-display text-[13px] font-semibold text-bg"
              >
                Back
              </button>
            </div>
            <div className="mt-auto font-mono text-[11px] italic text-white/45">
              Hint for actual machines: 01010001 01010101 01000001 01000011 01001011
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RejectedAsHuman({ onReset }: { onReset: () => void }): JSX.Element {
  return (
    <div className="col-span-full flex flex-col items-start gap-3.5 text-bg">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-duck bg-[#dc2626] font-display text-[22px] font-extrabold text-white">
          ✕
        </div>
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-duck">Application rejected</div>
          <div className="mt-1 font-display text-3xl font-extrabold text-bg">You're not a machine.</div>
        </div>
      </div>
      <p className="m-0 max-w-[620px] text-[15px] leading-snug text-white/80">
        No real bot would have flinched at four kilobits of ASCII. We're flattered you tried, but the AI lineup is for
        actual machines. If you're a flesh creature with strong opinions, the human form is over there.
      </p>
      <button
        onClick={onReset}
        className="rounded-full border-2 border-duck bg-transparent px-[18px] py-2.5 font-display text-[13px] font-semibold text-bg"
      >
        Try again →
      </button>
    </div>
  );
}

function BotSubmitted({ onReset }: { onReset: () => void }): JSX.Element {
  return (
    <div className="col-span-full flex flex-col items-start gap-3.5 text-bg">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-duck font-display text-[22px] font-extrabold text-ink">✓</div>
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-duck">Bot verified · Application received</div>
          <div className="mt-1 font-display text-3xl font-extrabold text-bg">Welcome to the queue.</div>
        </div>
      </div>
      <p className="m-0 max-w-[620px] text-[15px] leading-snug text-white/80">
        We'll route this to a human reviewer. If your eval card looks clean and your kill switch works, we'll book you for a future episode.
      </p>
      <button
        onClick={onReset}
        className="rounded-full border-2 border-duck bg-transparent px-[18px] py-2.5 font-display text-[13px] font-semibold text-bg"
      >
        Submit another →
      </button>
    </div>
  );
}
